/**
 * TASK-232: Extraction Prefill Store
 *
 * Zustand store for passing extraction data from DocumentsTab to ComplianceTab.
 * Solves the cross-tab communication problem since DocumentsTab is not wrapped
 * in ComplianceProvider.
 *
 * Flow:
 * 1. ExtractionPreviewModal approval → store mapped data here
 * 2. Navigate to ComplianceTab
 * 3. ComplianceTab detects pending data → calls ingestFromExtraction()
 * 4. Clear store after ingestion
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ExtractionResult, AllFormsData } from '../services/compliance';
import type { ClientComplianceData } from '../types/clientCompliance';

/**
 * Pending extraction data waiting to be ingested by ComplianceTab
 */
export interface PendingExtraction {
  /** Client ID this extraction belongs to */
  clientId: string;
  /** Raw extraction result from backend/mapper */
  extractionData: ExtractionResult;
  /** Pre-mapped form data for all 4 forms */
  mappedForms: AllFormsData;
  /** Pre-mapped compliance data for shared model */
  mappedCompliance: Partial<ClientComplianceData>;
  /** Source document name for toast notification */
  sourceName: string;
  /** When the extraction was approved */
  approvedAt: string;
  /** TASK-232: Raw KYC data from preview (more complete than extractionData) */
  rawKycData?: Record<string, unknown>;
  /** TASK-232: Raw Risk Profile data from preview */
  rawRiskData?: Record<string, unknown>;
}

interface ExtractionPrefillState {
  /** Pending extraction waiting to be processed */
  pendingExtraction: PendingExtraction | null;

  /** Set pending extraction after approval in ExtractionPreviewModal */
  setPendingExtraction: (data: PendingExtraction) => void;

  /** Clear pending extraction after ingestion in ComplianceTab */
  clearPendingExtraction: () => void;

  /** Check if there's pending extraction for a specific client */
  hasPendingExtraction: (clientId: string) => boolean;
}

export const useExtractionPrefillStore = create<ExtractionPrefillState>()(
  devtools(
    (set, get) => ({
      pendingExtraction: null,

      setPendingExtraction: (data) => {
        console.log('[ExtractionPrefillStore] Setting pending extraction:', {
          clientId: data.clientId,
          sourceName: data.sourceName,
          formCount: Object.keys(data.mappedForms).length,
        });
        set({ pendingExtraction: data });
      },

      clearPendingExtraction: () => {
        console.log('[ExtractionPrefillStore] Clearing pending extraction');
        set({ pendingExtraction: null });
      },

      hasPendingExtraction: (clientId) => {
        const pending = get().pendingExtraction;
        return pending !== null && pending.clientId === clientId;
      },
    }),
    { name: 'extraction-prefill-store' }
  )
);

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Get pending extraction (reactive)
 */
export const usePendingExtraction = () =>
  useExtractionPrefillStore((state) => state.pendingExtraction);

/**
 * Get store actions (non-reactive)
 */
export const getExtractionPrefillActions = () => ({
  setPendingExtraction: useExtractionPrefillStore.getState().setPendingExtraction,
  clearPendingExtraction: useExtractionPrefillStore.getState().clearPendingExtraction,
  hasPendingExtraction: useExtractionPrefillStore.getState().hasPendingExtraction,
});
